import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';

import styles from '@vp-ui/components/VpVideoCaptionPlayer/VpVideoCaptionPlayer.module.less';
import {
  CAPTION_PRESETS,
  DEFAULT_PRESET,
  HIGHLIGHT_MODES,
  DEFAULT_HIGHLIGHT_MODE,
  WORDS_PER_DISPLAY,
  DEFAULT_WORDS_PER_DISPLAY
} from '@src/constants/caption-presets';
import { CAPTION_LANGUAGES } from '@src/constants/caption-languages';

const getLanguageLabel = (code) => {
  if (!code) return 'Captions';
  if (code === 'auto') return 'Auto-detected';
  return CAPTION_LANGUAGES.find((l) => l.code === code)?.label || code;
};

const getOutlineStyle = (outline) => {
  if (!outline || outline === 'none') return 'none';
  const parts = outline.split(' ');
  if (parts.length < 2) return 'none';
  const size = parseInt(parts[0], 10) || 1;
  const color = parts[1];
  return `${-size}px ${-size}px 0 ${color}, ${size}px ${-size}px 0 ${color}, ${-size}px ${size}px 0 ${color}, ${size}px ${size}px 0 ${color}`;
};

const getCaptionStyles = (preset) => {
  const config = CAPTION_PRESETS[preset] || CAPTION_PRESETS[DEFAULT_PRESET];
  return {
    fontFamily: config.fontFamily,
    fontSize: `${config.fontSize}px`,
    color: config.fontColor,
    backgroundColor: config.bgColor,
    textShadow: getOutlineStyle(config.outline),
    padding: config.bgColor !== 'transparent' ? '4px 8px' : '2px 4px',
    borderRadius: config.bgColor !== 'transparent' ? '4px' : '0'
  };
};

export const VpVideoCaptionPlayer = ({
  mediaUrl,
  captions = [],
  onLoad,
  thumbnailUrl,
  onEnd = () => {},
  preset = DEFAULT_PRESET,
  highlightMode = DEFAULT_HIGHLIGHT_MODE,
  wordsPerDisplay = DEFAULT_WORDS_PER_DISPLAY,
  refreshKey = 0,
  ...rest
}) => {
  const videoPlayerRef = useRef(null);
  const [orientation, setOrientation] = useState(styles.landscape);
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  // useRef for currentTime avoids 60fps state updates; cue is computed in the RAF
  // handler and stored in state only when it actually changes
  const currentTimeRef = useRef(0);
  const wordLevelDataRef = useRef(null);
  const [wordLevelData, setWordLevelData] = useState(null);
  const [cueState, setCueState] = useState({ cue: null, wordIdx: -1 });
  const animFrameRef = useRef(null);

  const { cue: currentCue, wordIdx: activeWordIdx } = cueState;

  const completedCaptions = captions.filter((c) => c.status === 'completed');
  const activeCaption = completedCaptions[0];

  useEffect(() => {
    const video = videoPlayerRef.current;
    if (!video) return;
    const checkOrientation = () => {
      const aspectRatio = video.videoWidth / video.videoHeight;
      setOrientation(aspectRatio > 1 ? styles.landscape : styles.portrait);
    };
    video.addEventListener('loadedmetadata', checkOrientation);
    return () => video.removeEventListener('loadedmetadata', checkOrientation);
  }, [mediaUrl]);

  useEffect(() => {
    if (!activeCaption?.wordLevelUrl) return;

    let cancelled = false;
    fetch(activeCaption.wordLevelUrl, { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (!cancelled) {
          wordLevelDataRef.current = data;
          setWordLevelData(data);
        }
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load word-level captions:', err);
      });

    return () => { cancelled = true; };
    // refreshKey forces a re-fetch when whisper data is synced (same URL, new content)
  }, [activeCaption?.wordLevelUrl, refreshKey]);

  useEffect(() => {
    const video = videoPlayerRef.current;
    if (!video) return;

    // Computes cue and active word index from the current time and updates state
    // only when either value actually changes, avoiding unnecessary re-renders.
    const computeCue = (time) => {
      const data = wordLevelDataRef.current;
      setCueState((prev) => {
        if (!data?.cues) {
          return prev.cue === null && prev.wordIdx === -1 ? prev : { cue: null, wordIdx: -1 };
        }
        const newCue = data.cues.find((c) => time >= c.start && time <= c.end) ?? null;
        const newWordIdx = newCue?.words
          ? newCue.words.findIndex((w) => time >= w.start && time <= w.end)
          : -1;
        const cueChanged =
          prev.cue?.start !== newCue?.start || prev.cue?.end !== newCue?.end;
        const wordChanged = prev.wordIdx !== newWordIdx;
        if (!cueChanged && !wordChanged) return prev;
        return { cue: newCue, wordIdx: newWordIdx };
      });
    };

    const updateTime = () => {
      currentTimeRef.current = video.currentTime;
      computeCue(video.currentTime);
      animFrameRef.current = requestAnimationFrame(updateTime);
    };

    const onPlay = () => { animFrameRef.current = requestAnimationFrame(updateTime); };
    const onPause = () => { cancelAnimationFrame(animFrameRef.current); };
    const onSeeked = () => {
      currentTimeRef.current = video.currentTime;
      computeCue(video.currentTime);
    };

    video.addEventListener('play', onPlay);
    video.addEventListener('pause', onPause);
    video.addEventListener('seeked', onSeeked);

    if (!video.paused) {
      animFrameRef.current = requestAnimationFrame(updateTime);
    }

    return () => {
      video.removeEventListener('play', onPlay);
      video.removeEventListener('pause', onPause);
      video.removeEventListener('seeked', onSeeked);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, []);

  const handleOnLoad = () => {
    const video = videoPlayerRef.current;
    onLoad && onLoad(video.duration);
  };

  const presetConfig = CAPTION_PRESETS[preset] || CAPTION_PRESETS[DEFAULT_PRESET];
  const captionStyles = getCaptionStyles(preset);
  const useWordLevel = wordLevelData && wordLevelData.cues;

  const renderCaptionText = () => {
    if (!currentCue || !captionsEnabled) return null;
    // Read the current ref value at render time; renders are triggered only when
    // cue or active word changes, so this is always accurate.
    const currentTime = currentTimeRef.current;

    if (highlightMode === HIGHLIGHT_MODES.WORD_BY_WORD) {
      const words = currentCue.words;
      const activeIdx = activeWordIdx;

      if (wordsPerDisplay === WORDS_PER_DISPLAY.THREE) {
        // Determine the base word index to calculate which group of 3 to show.
        // If a word is active, use it. If between words, use the last spoken word
        // so the group doesn't flip until the next group actually starts.
        let baseIdx = activeIdx;
        if (baseIdx === -1) {
          let lastDoneIdx = -1;
          for (let i = words.length - 1; i >= 0; i--) {
            if (currentTime > words[i].end) {
              lastDoneIdx = i;
              break;
            }
          }
          baseIdx = lastDoneIdx === -1 ? 0 : lastDoneIdx;
        }

        const groupIndex = Math.floor(baseIdx / 3);
        const groupStart = groupIndex * 3;
        const groupWords = words.slice(groupStart, groupStart + 3);

        return (
          <span>
            {groupWords.map((w, sliceIdx) => {
              const isActive = groupStart + sliceIdx === activeIdx;
              return (
                <span
                  key={`${w.start}-${sliceIdx}`}
                  style={{
                    color: isActive ? presetConfig.highlightColor : presetConfig.fontColor,
                    ...(isActive && presetConfig.highlightBgColor && {
                      backgroundColor: presetConfig.highlightBgColor,
                      borderRadius: '3px',
                      padding: '0 2px'
                    }),
                    transition: 'color 0.1s ease'
                  }}
                >
                  {sliceIdx > 0 ? ' ' : ''}{w.word}
                </span>
              );
            })}
          </span>
        );
      }

      if (activeIdx === -1) {
        const nextWord = words.find((w) => currentTime < w.start);
        if (nextWord) return <span style={{ color: presetConfig.fontColor }}>{nextWord.word}</span>;
        const lastWord = words[words.length - 1];
        return <span style={{ color: presetConfig.fontColor }}>{lastWord?.word || ''}</span>;
      }

      return (
        <span style={{ color: presetConfig.highlightColor }}>
          {words[activeIdx].word}
        </span>
      );
    }

    return (
      <span>
        {currentCue.words.map((w, i) => {
          const isActive = currentTime >= w.start && currentTime <= w.end;
          const wordStyle = isActive
            ? {
                color: presetConfig.highlightColor,
                ...(presetConfig.highlightBgColor && {
                  backgroundColor: presetConfig.highlightBgColor,
                  borderRadius: '3px',
                  padding: '0 2px'
                }),
                transition: 'color 0.1s ease'
              }
            : { color: presetConfig.fontColor, transition: 'color 0.1s ease' };
          return (
            <span key={`${w.start}-${i}`} style={wordStyle}>
              {i > 0 ? ' ' : ''}
              {w.word}
            </span>
          );
        })}
      </span>
    );
  };

  return (
    <section className={styles.vpCaptionPlayerContainer}>
      <div className={`${styles.vpVideoContainer} ${orientation}`}>
        <video
          ref={videoPlayerRef}
          onLoadedData={handleOnLoad}
          controls
          onEnded={onEnd}
          {...(thumbnailUrl ? { poster: thumbnailUrl } : {})}
          {...rest}
        >
          <source src={mediaUrl} key={mediaUrl} />
          {!useWordLevel &&
            completedCaptions
              .filter((c) => c.vttUrl)
              .map((caption, index) => (
                <track
                  key={caption.id}
                  kind="captions"
                  src={caption.vttUrl}
                  srcLang={caption.isTranslation
                    ? (caption.targetLanguage || 'en')
                    : (caption.sourceLanguage || caption.targetLanguage || 'en')}
                  label={getLanguageLabel(caption.isTranslation
                    ? caption.targetLanguage
                    : (caption.sourceLanguage || caption.targetLanguage))}
                  default={index === 0}
                />
              ))}
        </video>
        {useWordLevel && currentCue && captionsEnabled && (
          <div className={styles.captionOverlay}>
            <div className={styles.captionText} style={captionStyles}>
              {renderCaptionText()}
            </div>
          </div>
        )}
      </div>
      {completedCaptions.length > 0 && (
        <div className={styles.controlsBar}>
          <Button
            type="button"
            size="small"
            variant={captionsEnabled ? 'contained' : 'outlined'}
            aria-label="Toggle closed captions"
            aria-pressed={captionsEnabled}
            onClick={() => setCaptionsEnabled(!captionsEnabled)}
            sx={{ fontWeight: 700, textTransform: 'none' }}
          >
            CC
          </Button>
        </div>
      )}
    </section>
  );
};

VpVideoCaptionPlayer.propTypes = {
  mediaUrl: PropTypes.string,
  captions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      vttUrl: PropTypes.string,
      wordLevelUrl: PropTypes.string,
      sourceLanguage: PropTypes.string,
      targetLanguage: PropTypes.string,
      status: PropTypes.string
    })
  ),
  thumbnailUrl: PropTypes.string,
  onLoad: PropTypes.func,
  onEnd: PropTypes.func,
  preset: PropTypes.string,
  highlightMode: PropTypes.string,
  wordsPerDisplay: PropTypes.number,
  refreshKey: PropTypes.number
};

export default VpVideoCaptionPlayer;



// WEBPACK FOOTER //
// ./src/vp-ui/components/VpVideoCaptionPlayer/VpVideoCaptionPlayer.jsx